"use client"

import { useState, useCallback } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { IGenerateNFFResponse } from "@/@types/nff"
import SingleDatePicker from "components/input-range-data"
import {
    useDownloadNFFModelMutation,
    useGenerateNFFMutation,
    useSendNFFExcelMutation,
} from "@/services/nff"
import { Button } from "components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card"
import { Input } from "components/ui/input"
import { Label } from "@radix-ui/react-dropdown-menu"
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form"
import { toast } from "components/ui/use-toast"
import { Calendar, Download, FileText, Upload, X } from "lucide-react"
import {
    generateNffGnreSchema,
    GenerateNffGnreFormData,
    NffTabType
} from "./validate-form"

export function GenerateNffModal() {
    const [activeTab, setActiveTab] = useState<NffTabType>("gnre")
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [resetInput, setResetInput] = useState(0)

    const [generateNff, { isLoading: isGenerating }] = useGenerateNFFMutation()
    const [sendNffExcel, { isLoading: isUploading }] = useSendNFFExcelMutation()
    const [downloadModel] = useDownloadNFFModelMutation()

    const gnreForm = useForm<GenerateNffGnreFormData>({
        resolver: zodResolver(generateNffGnreSchema),
        defaultValues: {
            startDate: undefined,
            endDate: undefined,
        },
    });

    const isDisable =
        isGenerating ||
        isUploading ||
        (activeTab === "gnre" && !gnreForm.formState.isValid) ||
        (activeTab === "outros" && !selectedFile)

    const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0])
        }
    }, [])

    const handleRemoveFile = useCallback(() => {
        setSelectedFile(null)
        setResetInput((prev) => prev + 1)
    }, [])

    const handleDownloadModel = async () => {
        try {
            const { excelFile } = await downloadModel().unwrap()

            const a = document.createElement("a")
            a.href = excelFile
            a.download = "modelo_nff.xlsx"
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)

            toast({
                description: "Modelo baixado com sucesso!",
                duration: 3000,
            })
        } catch (error) {
            toast({
                variant: "destructive",
                description: "Erro ao baixar o modelo de NFF.",
                duration: 5000,
            })
            console.error("Erro ao baixar modelo NFF:", error)
        }
    }

    const handleGnreSubmit = async (data: GenerateNffGnreFormData) => {
        try {
            const response: IGenerateNFFResponse = await generateNff({
                params: {
                    startDate: data.startDate.toISOString().split("T")[0],
                    endDate: data.endDate.toISOString().split("T")[0],
                },
            }).unwrap()

            const { totalSent, successCount, errorCount, errorInvoices } = response.result

            toast({
                description: `Foram enviadas ${totalSent} notas fiscais. ${successCount} processadas com sucesso. ${errorCount} apresentaram erro.`,
                duration: 6000,
            })

            if (errorCount > 0) {
                toast({
                    variant: "destructive",
                    description: `As seguintes notas apresentaram erro: ${errorInvoices.join(", ")}`,
                    duration: 8000,
                })
            }

            gnreForm.reset()
        } catch (error) {
            toast({
                variant: "destructive",
                description: "Erro ao processar a Nota Fiscal. Verifique os dados e tente novamente.",
                duration: 5000,
            })
            console.error("Erro ao processar NFF:", error)
        }
    }

    const handleFileSubmit = async () => {
        if (!selectedFile) {
            toast({
                variant: "destructive",
                description: "Selecione um arquivo antes de prosseguir.",
                duration: 5000,
            })
            return
        }

        try {
            const formData = new FormData()
            formData.append("files", selectedFile)

            const response = await sendNffExcel(formData).unwrap()
            const { totalSent, successCount, errorCount, errorInvoices } = response.result

            toast({
                description: `Arquivo processado! Total enviado: ${totalSent}, Sucesso: ${successCount}, Erros: ${errorCount}.`,
                duration: 6000,
            })

            if (errorCount > 0) {
                toast({
                    variant: "destructive",
                    description: `Erros encontrados: ${errorInvoices.join(", ")}`,
                    duration: 8000,
                })
            }

            setSelectedFile(null)
            setResetInput((prev) => prev + 1)
        } catch (error) {
            toast({
                variant: "destructive",
                description: "Erro ao processar a Nota Fiscal. Verifique os dados e tente novamente.",
                duration: 5000,
            })
            console.error("Erro ao processar NFF:", error)
        }
    }

    const handleSubmit = () => {
        toast({
            description: "Processando...",
            duration: 3000,
        })

        if (activeTab === "gnre") {
            gnreForm.handleSubmit(handleGnreSubmit)()
        } else {
            handleFileSubmit()
        }
    }

    return (
        <div className="flex flex-col w-full h-[600px] min-w-[600px]">
            <div className="flex space-x-1 mb-6 bg-muted/0 p-1 rounded-lg">
                <Button
                    variant={activeTab === "gnre" ? "default" : "outline"}
                    onClick={() => setActiveTab("gnre")}
                    className="flex-1 flex items-center gap-2 h-12 font-semibold text-md"
                >
                    <FileText className="h-4 w-4" />
                    GNRE
                </Button>
                <Button
                    variant={activeTab === "outros" ? "default" : "outline"}
                    onClick={() => setActiveTab("outros")}
                    className="flex-1 flex items-center gap-2 h-12 font-semibold text-md"
                >
                    <Upload className="h-4 w-4" />
                    Outros NFFs
                </Button>
            </div>

            <div className="flex-1 flex flex-col space-y-4">
                {activeTab === "gnre" && (
                    <Card className="flex-1">
                        <CardHeader className="border-b">
                            <CardTitle className="flex items-center gap-2">
                                <Calendar className="h-5 w-5" />
                                Configuração GNRE
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <Form {...gnreForm}>
                                <div className="space-y-6">
                                    <div className="pt-2">
                                        <Label className="text-base font-semibold block mb-4">
                                            Período de Processamento
                                        </Label>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <FormField
                                                control={gnreForm.control}
                                                name="startDate"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <label htmlFor="initial_term" className="text-sm text-muted-foreground block mb-2">
                                                            Data Inicial
                                                        </label>
                                                        <FormControl>
                                                            <SingleDatePicker
                                                                id="initial_term"
                                                                date={field.value}
                                                                onDateChange={field.onChange}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={gnreForm.control}
                                                name="endDate"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <label htmlFor="final_term" className="text-sm text-muted-foreground block mb-2">
                                                            Data Final
                                                        </label>
                                                        <FormControl>
                                                            <SingleDatePicker
                                                                id="final_term"
                                                                date={field.value}
                                                                onDateChange={field.onChange}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        </CardContent>
                    </Card>
                )}

                {activeTab === "outros" && (
                    <Card className="flex-1">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="flex items-center gap-2">
                                <Upload className="h-5 w-5" />
                                Upload de Arquivo
                            </CardTitle>
                            <Button
                                variant="outline"
                                onClick={handleDownloadModel}
                                className="flex items-center gap-2 bg-transparent"
                            >
                                <Download className="h-4 w-4" />
                                Exportar modelo
                            </Button>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label>Arquivo de NFF</Label>
                                <div className="flex items-center justify-center w-full h-32 border-2 border-dashed border-muted-foreground/25 rounded-lg bg-muted/50 hover:bg-muted/75 transition-colors">
                                    <label
                                        htmlFor="nff-file"
                                        className="w-full h-full flex flex-col items-center justify-center cursor-pointer text-muted-foreground"
                                    >
                                        <Upload className="h-8 w-8 mb-2" />
                                        <span className="text-sm font-medium">
                                            {selectedFile ? selectedFile.name : "Clique para selecionar arquivo"}
                                        </span>
                                        <span className="text-xs text-muted-foreground mt-1">
                                            Formatos: .txt, .csv, .xlsx
                                        </span>
                                    </label>
                                    <Input
                                        id="nff-file"
                                        type="file"
                                        accept=".txt,.csv,.xlsx"
                                        disabled={isUploading}
                                        onChange={handleFileChange}
                                        className="hidden"
                                        key={`file-${resetInput}`}
                                    />
                                </div>
                            </div>

                            {selectedFile && (
                                <Card className="bg-muted/50">
                                    <CardContent className="flex items-center justify-between p-4">
                                        <div className="flex items-center gap-2">
                                            <FileText className="h-4 w-4 text-muted-foreground" />
                                            <span className="text-sm font-medium truncate">{selectedFile.name}</span>
                                        </div>
                                        <Button variant="ghost" size="sm" onClick={handleRemoveFile} className="h-8 w-8 p-0">
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </CardContent>
                                </Card>
                            )}
                        </CardContent>
                    </Card>
                )}

                {activeTab && (
                    <Card>
                        <CardContent className="p-4">
                            <Button
                                className="w-full h-12 font-semibold text-base"
                                onClick={handleSubmit}
                                disabled={isDisable}
                            >
                                {isGenerating || isUploading ? "Processando..." : "Processar NFF"}
                            </Button>
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    )
}
